import { FC, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Widget, WidgetEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Add as AddIcon } from '@factorialco/f0-react/icons/app'
import {
  UpcomingMeetingsInsightDocument,
  UpcomingMeetingsInsightQuery,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import CalendarEventList from 'modules/core/components/CalendarEventList'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import NewMeetingModal from 'modules/one_on_ones/components/NewMeetingModal'

const useLinkAndTitle = () => {
  const title = i18n.t('insights.widgets.one_on_ones.title')

  const { navigation } = useNavigation()

  const linkUrl = navigation.performanceOneOnOnes.fullPath()

  return {
    title,
    link: {
      url: linkUrl,
      title,
    },
  }
}

const prepareData = (data: UpcomingMeetingsInsightQuery) => {
  const list = compact(data.oneOnOnes.meetingsConnection.edges.map((edge) => edge))
  return {
    list,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const OneOnOnesWidgetComponent: FC<{ data: PreparedData }> = ({ data: { list } }) => {
  const { link, title } = useLinkAndTitle()

  const [showNewModal, setNewAddModal] = useState(false)

  const handleClickAddEvent = () => {
    setNewAddModal(true)
  }

  return (
    <Widget
      header={{
        title,
        link,
      }}
      action={{
        label: i18n.t('insights.widgets.one_on_ones.add_meeting'),
        onClick: handleClickAddEvent,
      }}
    >
      <Box>
        <CalendarEventList items={list} />
        {showNewModal && (
          <NewMeetingModal
            onClose={() => setNewAddModal(false)}
            refetchQueries={[UpcomingMeetingsInsightDocument]}
          />
        )}
      </Box>
    </Widget>
  )
}

const EmptyComponent = () => {
  const { link, title } = useLinkAndTitle()

  const [showNewModal, setNewAddModal] = useState(false)

  const handleClickAddEvent = () => {
    setNewAddModal(true)
  }

  return (
    <Widget header={{ title, link }}>
      <Box>
        <WidgetEmptyState
          title={i18n.t('insights.widgets.one_on_ones.empty.title')}
          description={i18n.t('insights.widgets.one_on_ones.empty.description')}
          emoji="📝"
          actions={[
            {
              label: i18n.t('insights.widgets.one_on_ones.empty.add_meeting'),
              icon: AddIcon,
              variant: 'outline',
              onClick: handleClickAddEvent,
            },
          ]}
        />
        {showNewModal && (
          <NewMeetingModal
            onClose={() => setNewAddModal(false)}
            refetchQueries={[UpcomingMeetingsInsightDocument]}
          />
        )}
      </Box>
    </Widget>
  )
}

export const buildOneOnOnesWidget = () =>
  buildWidget({
    id: 'one_on_ones',
    document: UpcomingMeetingsInsightDocument,
    emptyComponent: () => <EmptyComponent />,
    isEmpty: ({ list }) => !list.length,
    prepareData,
    component: OneOnOnesWidgetComponent,
  })
